// Formular zum Erstellen/Bearbeiten eines Albums (EE-Release).
// Create-Modus: nur Metadaten (Titel/Artist/Beschreibung/Jahr/Cover) → nach dem Anlegen wechselt der
// Parent in den Edit-Modus, damit Produkte kuratiert werden. Edit-Modus: zusätzlich Publish-Toggle +
// Produkt-Kuratierung (hinzufügen/entfernen/umsortieren via setContent — ganze Liste, ein Call).
import React, { useEffect, useId, useState } from 'react';
import { PhotoIcon, TrashIcon, PlusIcon, ArrowUpIcon, ArrowDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { BunnyFilePicker } from '../admin/BunnyFilePicker';
import { ProductPicker } from '../albums/ProductPicker';
import { Rueckfrage } from '../ui/Rueckfrage';
import { useRueckfrage } from '../../hooks/useRueckfrage';
import { albumService } from '../../services/albumService';
import { artistService } from '../../services/artistService';
import { Album } from '../../types/album';
import { Artist } from '../../types/artist';
import { TrackKindLabels } from '../../types/audio';
import type { BenachrichtigungsTyp } from '../ui/Benachrichtigung';

interface AlbumFormProps {
  initial: Album | null; // null = Create-Modus
  onCreated: (a: Album) => void;
  onClose: () => void;
  onDeleted: (id: string) => void;
  /** Meldet an den Balken der Elternseite. Tonlage aus `BenachrichtigungsTyp` — nicht abschreiben. */
  notify: (type: BenachrichtigungsTyp, message: string) => void;
}

export const AlbumForm: React.FC<AlbumFormProps> = ({ initial, onCreated, onClose, onDeleted, notify }) => {
  const isEdit = !!initial;
  // Siehe `ui/Filterleiste.tsx`: erzeugte Wurzel statt fester Zeichenketten, damit zwei
  // Formulare im selben Baum keine doppelten `id`-Werte erzeugen.
  const feldId = useId();
  const [al, setAl] = useState<Album | null>(initial);
  const [title, setTitle] = useState(initial?.title ?? '');
  const [artistId, setArtistId] = useState(initial?.artistId ?? initial?.artistRef?.id ?? ''); // kuratierter Interpret; '' = keiner
  const [artists, setArtists] = useState<Artist[]>([]);
  const [description, setDescription] = useState(initial?.description ?? '');
  const [releaseYear, setReleaseYear] = useState(initial?.releaseYear ? String(initial.releaseYear) : '');
  const [coverImageUrl, setCoverImageUrl] = useState(initial?.coverImageUrl ?? '');
  const [saving, setSaving] = useState(false);
  const [mutating, setMutating] = useState(false);
  const [showCoverPicker, setShowCoverPicker] = useState(false);
  const [showProductPicker, setShowProductPicker] = useState(false);

  // Sicherheitsabfragen dieses Formulars. Das Feld heißt `rückfrage` und nicht `inhalt`, weil
  // `inhalt` in den umgestellten Dateien bereits für den Benachrichtigungs-Balken belegt ist.
  //
  // ⚠️ Zur Frage, ob zwischen Frage und Ausführung etwas veralten kann — gemessen, nicht
  // angenommen: solange eine Rückfrage offen steht, deckt ihr Schleier die ganze Seite
  // (`fixed inset-0`, `z-[55]`, über dem `z-50` jedes Overlays) und der Fokus ist im Dialog
  // gefangen. Ein zweiter Klick auf „Veröffentlichen", „Löschen" oder eine Zeilen-Aktion ist
  // währenddessen nicht auslösbar, `mutating` kann also nicht hinter dem `await` umspringen. Der
  // einzige verbleibende Weg — die Nutzerin wechselt über das Menü die Seite — ist im Haken
  // abgefangen: er beantwortet eine offene Frage beim Abbau mit `false`.
  const { rückfrage, frage, antworte } = useRueckfrage();

  // Kuratierte Interpreten fürs Dropdown (Admin-Liste, alle Status).
  useEffect(() => {
    let alive = true;
    artistService
      .getArtists()
      .then((list) => { if (alive) setArtists(list); })
      .catch(() => { /* Dropdown bleibt leer — Interpret bleibt optional */ });
    return () => { alive = false; };
  }, []);

  const existingContentIds = new Set((al?.items ?? []).map((i) => i.contentId));
  const contentIds = (al?.items ?? []).map((i) => i.contentId);

  const handleSaveMeta = async () => {
    if (!title.trim()) {
      notify('error', 'Titel ist erforderlich');
      return;
    }
    const year = releaseYear.trim() ? parseInt(releaseYear.trim(), 10) : null;
    setSaving(true);
    try {
      const payload = {
        title: title.trim(),
        artistId, // kuratierter Interpret ('' = keiner → Backend nutzt Fallback „Energetic Eternity")
        description: description.trim() || null,
        coverImageUrl: coverImageUrl.trim() || null,
        releaseYear: Number.isFinite(year as number) ? year : null,
      };
      if (isEdit && al) {
        const updated = await albumService.updateAlbum(al.id, payload);
        setAl(updated);
        notify('success', 'Gespeichert');
      } else {
        const created = await albumService.createAlbum(payload);
        notify('success', 'Album erstellt — jetzt Produkte hinzufügen');
        onCreated(created);
      }
    } catch {
      notify('error', 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const setStatus = async (status: 'DRAFT' | 'PUBLISHED') => {
    if (!al || mutating) return;
    // Beide Fragen bewusst OHNE `gefahr`: veröffentlicht wird nichts zerstört, nur die
    // Sichtbarkeit umgestellt — und derselbe Knopf heißt danach „Zurückziehen" und stellt sie
    // zurück. Rot wäre hier eine Warnung, die nichts warnt, und entwertete die Farbe an der
    // einen Stelle in dieser Datei, an der sie zählt (Löschen).
    if (status === 'PUBLISHED' && al.items.length === 0) {
      const trotzdem = await frage({
        titel: 'Album ohne Produkte veröffentlichen?',
        text:
          'Dieses Album enthält noch keine Produkte. Veröffentlicht ist es im Portal für alle '
          + 'sichtbar, aber leer — es gibt nichts darin zum Anhören.\n\n'
          + 'Rückgängig zu machen ist das jederzeit über „Zurückziehen".',
        bestätigenText: 'Trotzdem veröffentlichen',
      });
      if (!trotzdem) return;
    }
    if (status === 'PUBLISHED' && !al.coverImageUrl) {
      const trotzdem = await frage({
        titel: 'Album ohne Cover veröffentlichen?',
        text:
          'Dieses Album hat kein Coverbild. Veröffentlicht erscheint es im Portal ohne eigenes '
          + 'Bild.\n\nRückgängig zu machen ist das jederzeit über „Zurückziehen".',
        bestätigenText: 'Trotzdem veröffentlichen',
      });
      if (!trotzdem) return;
    }
    setMutating(true);
    try {
      const updated = await albumService.updateAlbum(al.id, { status });
      setAl(updated);
      notify('success', status === 'PUBLISHED' ? 'Veröffentlicht' : 'Zurückgezogen (Entwurf)');
    } catch {
      notify('error', 'Statuswechsel fehlgeschlagen');
    } finally {
      setMutating(false);
    }
  };

  const applyContent = async (ids: string[]) => {
    if (!al || mutating) return;
    setMutating(true);
    try {
      const updated = await albumService.setContent(al.id, ids);
      setAl(updated);
    } catch {
      notify('error', 'Produkte konnten nicht aktualisiert werden');
    } finally {
      setMutating(false);
    }
  };

  const handleAddProduct = async (contentId: string) => {
    if (contentIds.includes(contentId)) return;
    await applyContent([...contentIds, contentId]);
  };

  const handleRemoveProduct = async (contentId: string) => {
    await applyContent(contentIds.filter((id) => id !== contentId));
  };

  const move = async (index: number, dir: -1 | 1) => {
    const ids = [...contentIds];
    const j = index + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[index], ids[j]] = [ids[j], ids[index]];
    await applyContent(ids);
  };

  const handleDelete = async () => {
    if (!al) return;
    // Der Stand wird VOR der Frage festgehalten und danach benutzt: gelöscht wird genau das
    // Album, dessen Titel im Dialog stand. Läse der Löschpfad hinter dem `await` erneut aus dem
    // Zustand, könnte der Dialog das eine benennen und der Aufruf das andere treffen.
    const { id, title: titelZurZeitDerFrage } = al;
    // `gefahr: true` — die einzige Stelle dieser Datei, an der etwas unwiderruflich verschwindet.
    // Die Folgen sind am Backend nachgelesen (`albumService.deleteAlbum`): `content.albumId`
    // läuft auf SetNull, die Produkte selbst bleiben; `album_saves` kaskadieren, und
    // `deleteLibraryTarget` räumt Merkzeichen und Ordner-Einträge bei ALLEN Nutzern ab.
    const bestätigt = await frage({
      titel: 'Album endgültig löschen?',
      text:
        `„${titelZurZeitDerFrage}" wird gelöscht. Das lässt sich nicht rückgängig machen.\n\n`
        + 'Die enthaltenen Produkte bleiben bestehen — sie verlieren nur die Zuordnung zu diesem '
        + 'Album. Bei allen, die das Album gemerkt oder in einen Ordner gelegt haben, '
        + 'verschwindet es aus der Bibliothek.',
      gefahr: true,
    });
    if (!bestätigt) return;
    try {
      await albumService.deleteAlbum(id);
      onDeleted(id);
    } catch {
      notify('error', 'Löschen fehlgeschlagen');
    }
  };

  return (
    <div className="space-y-6">
      {/* Metadaten */}
      <div className="card p-6 space-y-4">
        <div>
          <label htmlFor={`${feldId}-titel`} className="block text-sm font-medium text-secondary-700 mb-2">Titel *</label>
          <input id={`${feldId}-titel`} type="text" value={title} onChange={(e) => setTitle(e.target.value)} className="input w-full" placeholder="z. B. Heart Code Bundle" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor={`${feldId}-interpret`} className="block text-sm font-medium text-secondary-700 mb-2">Interpret</label>
            <select id={`${feldId}-interpret`} value={artistId} onChange={(e) => setArtistId(e.target.value)} className="input w-full">
              <option value="">— kein Interpret —</option>
              {artists.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.name}{a.status !== 'PUBLISHED' ? ' (Entwurf)' : ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor={`${feldId}-jahr`} className="block text-sm font-medium text-secondary-700 mb-2">Jahr</label>
            <input id={`${feldId}-jahr`} type="number" value={releaseYear} onChange={(e) => setReleaseYear(e.target.value)} className="input w-full" placeholder="2026" />
          </div>
        </div>
        <div>
          <label htmlFor={`${feldId}-beschreibung`} className="block text-sm font-medium text-secondary-700 mb-2">Beschreibung</label>
          <textarea id={`${feldId}-beschreibung`} value={description} onChange={(e) => setDescription(e.target.value)} rows={3} className="input w-full" placeholder="Worum geht es in diesem Album?" />
        </div>
        <div>
          {/* Gruppe statt Feld: unter „Cover" stehen nur Schaltflächen (Mediathek, entfernen),
              kein Eingabefeld. Ein `htmlFor` hätte hier kein Ziel. */}
          <span id={`${feldId}-cover`} className="block text-sm font-medium text-secondary-700 mb-2">Cover</span>
          <div role="group" aria-labelledby={`${feldId}-cover`} className="flex items-center gap-4">
            {coverImageUrl ? (
              <img src={coverImageUrl} alt="Cover" className="h-20 w-20 rounded-lg object-cover" />
            ) : (
              <div className="h-20 w-20 rounded-lg bg-secondary-100 flex items-center justify-center">
                <PhotoIcon className="w-8 h-8 text-secondary-400" />
              </div>
            )}
            <div className="flex flex-col gap-2">
              <button type="button" onClick={() => setShowCoverPicker(true)} className="btn btn-outline">Aus Mediathek wählen</button>
              {coverImageUrl && (
                <button type="button" onClick={() => setCoverImageUrl('')} className="inline-flex items-center text-sm text-error-600 hover:text-error-800">
                  <XMarkIcon className="w-4 h-4 mr-1" /> Cover entfernen
                </button>
              )}
            </div>
          </div>
        </div>
        <div className="flex gap-3 pt-2">
          <button type="button" onClick={handleSaveMeta} disabled={saving} className="btn btn-primary disabled:opacity-50">
            {saving ? 'Speichern…' : isEdit ? 'Änderungen speichern' : 'Album anlegen'}
          </button>
          <button type="button" onClick={onClose} className="btn btn-outline">{isEdit ? 'Fertig' : 'Abbrechen'}</button>
        </div>
      </div>

      {/* Status + Produkte nur im Edit-Modus */}
      {isEdit && al && (
        <>
          <div className="card p-6">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-sm font-semibold text-secondary-900">Sichtbarkeit</h3>
                <p className="mt-1 text-sm text-secondary-600">
                  {al.status === 'PUBLISHED' ? (
                    <span className="inline-flex items-center"><span className="badge badge-primary mr-2">Veröffentlicht</span>Im Portal für alle sichtbar.</span>
                  ) : (
                    <span className="inline-flex items-center"><span className="badge badge-secondary mr-2">Entwurf</span>Noch nicht sichtbar — nur hier im CMS.</span>
                  )}
                </p>
              </div>
              {al.status === 'PUBLISHED' ? (
                <button type="button" onClick={() => setStatus('DRAFT')} disabled={mutating} className="btn btn-outline disabled:opacity-50">Zurückziehen</button>
              ) : (
                <button type="button" onClick={() => setStatus('PUBLISHED')} disabled={mutating} className="btn btn-primary disabled:opacity-50">Veröffentlichen</button>
              )}
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-sm font-semibold text-secondary-900">
                Produkte <span className="text-secondary-400">({al.items.length})</span>
              </h3>
              <button type="button" onClick={() => setShowProductPicker(true)} disabled={mutating} className="btn btn-primary disabled:opacity-50">
                <PlusIcon className="w-5 h-5 mr-2" />Produkt hinzufügen
              </button>
            </div>

            {al.items.length === 0 ? (
              <p className="py-8 text-center text-secondary-500">Noch keine Produkte. Fügen Sie welche über „Produkt hinzufügen" hinzu.</p>
            ) : (
              <ul className="divide-y divide-secondary-100">
                {al.items.map((item, index) => (
                  <li key={item.id} className="flex items-center gap-3 py-3">
                    <span className="w-6 text-right text-sm text-secondary-400">{index + 1}</span>
                    {item.coverImage ? (
                      <img src={item.coverImage} alt="" className="h-10 w-10 rounded object-cover" />
                    ) : (
                      <div className="h-10 w-10 rounded bg-secondary-100 flex items-center justify-center"><PhotoIcon className="w-5 h-5 text-secondary-400" /></div>
                    )}
                    <div className="flex-1 min-w-0">
                      <div className="truncate text-sm font-medium text-secondary-900">{item.title}</div>
                      <div className="text-xs text-secondary-500">
                        {TrackKindLabels[item.kind] || item.kind}{item.artist ? ` · ${item.artist}` : ''}
                      </div>
                    </div>
                    <div className="flex items-center gap-1">
                      <button type="button" onClick={() => void move(index, -1)} disabled={mutating || index === 0} className="p-1 text-secondary-500 hover:text-secondary-800 disabled:opacity-30" title="Nach oben">
                        <ArrowUpIcon className="w-4 h-4" />
                      </button>
                      <button type="button" onClick={() => void move(index, 1)} disabled={mutating || index === al.items.length - 1} className="p-1 text-secondary-500 hover:text-secondary-800 disabled:opacity-30" title="Nach unten">
                        <ArrowDownIcon className="w-4 h-4" />
                      </button>
                      <button type="button" onClick={() => void handleRemoveProduct(item.contentId)} disabled={mutating} className="p-1 text-error-500 hover:text-error-700 disabled:opacity-30" title="Entfernen">
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="flex justify-end">
            <button type="button" onClick={handleDelete} className="inline-flex items-center text-sm text-error-600 hover:text-error-800">
              <TrashIcon className="w-4 h-4 mr-1" /> Album löschen
            </button>
          </div>
        </>
      )}

      <BunnyFilePicker
        isOpen={showCoverPicker}
        onClose={() => setShowCoverPicker(false)}
        fileType="image"
        title="Cover auswählen"
        onSelect={(url) => {
          setCoverImageUrl(url);
          setShowCoverPicker(false);
        }}
      />

      {showProductPicker && al && (
        <ProductPicker
          existingContentIds={existingContentIds}
          onAdd={handleAddProduct}
          onClose={() => setShowProductPicker(false)}
        />
      )}

      {/* Der Dialog steht ganz unten, hängt sich aber per Portal an `document.body` — die Stelle
          im Baum entscheidet hier nichts über die Darstellung, wohl aber über die Lesbarkeit:
          er gehört zu den beiden anderen Overlays dieses Formulars. Ohne offene Frage rendert er
          NICHTS, `space-y-6` der Hülle bekommt also kein leeres Geschwisterkind. */}
      <Rueckfrage rückfrage={rückfrage} onAntwort={antworte} />
    </div>
  );
};
